import { useCallback, useEffect, useRef, useState } from "react";

import { fetchPortalShareSummary } from "../../service/portalShare";

/**
 * Whether this file is currently readable by anyone outside the firm.
 *
 * Read on every visit to a matter, so it fails quietly: if the request
 * errors the badge simply does not appear. The alternative — an error
 * state on the matter page — would be a worse trade, because this is a
 * supporting signal and the page it sits on is the one people work in all
 * day.
 *
 * It deliberately does **not** claim "not shared" on failure. `count` is
 * null until we actually know, so nothing on screen can assert a file is
 * private on the strength of a failed request.
 */
export const useMatterShareSummary = (matterId, refreshToken = 0) => {
  const [summary, setSummary] = useState(null);
  const latest = useRef(0);

  const load = useCallback(async () => {
    if (!matterId) {
      setSummary(null);
      return;
    }
    // Whichever request was started last wins, whatever order they come
    // back in. Someone clicking down a list of matters starts several,
    // and an answer about the file they have left would be worse than no
    // answer: this badge exists to say a *particular* file is exposed.
    const ticket = latest.current + 1;
    latest.current = ticket;
    try {
      const response = await fetchPortalShareSummary(matterId);
      if (ticket !== latest.current) return;
      setSummary(response.data);
    } catch {
      if (ticket !== latest.current) return;
      setSummary(null);
    }
  }, [matterId]);

  // Forget the previous file's answer the instant the file changes, so no
  // badge outlives the matter it was about while the new answer is still
  // in flight. Deliberately keyed on the matter alone — a refresh after
  // sharing must not blink the badge off and on.
  useEffect(() => {
    setSummary(null);
  }, [matterId]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  return {
    count: summary ? summary.share_count : null,
    recipients: summary?.recipients || [],
    sharedFolderIds: summary?.shared_folder_ids || [],
    reload: load,
  };
};

export default useMatterShareSummary;
